import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { cleanErrorState, bookConversationClass, closeBookingConversationModal } from '../../../../actions/new-booking';

import { selectProductType } from '../../../../reducers/order-info';
import { setSelectedDate } from '../../../../actions/bookable';

import { getDateFromIsoString, getTimeFromIsoString } from '../../../../utils/time-helpers';
import SessionsManagementModal from '../SessionsManagementModal';
import BookingSummaryItem from '../BookingSummaryItem';
import mixpanel from '../../../../utils/mixpanel-helper';

import { CLASS_TYPES } from '../../Enum';
import ErrorToast from '../../../design-system/toast/errorToast/ErrorToast';
import SessionsAlert from '../sessions-alert/sessions-alert';
import { getMonthTranslated } from '../../../design-system/calendar/TimeRelated.enum';
import useInitRecording from '../../../../hooks/useInitRecording';
import mixpanelHelper from '../../../../utils/mixpanel-helper';

const BookingConvoClassModal = ({
  availableSessionsThisMonth,
  bookConversationClassAction,
  calendar,
  cleanErrorStateAction,
  currentLang,
  daysAvailableThisMonth,
  errorMessage,
  hasErrorOnBooking,
  isCreatingAppointment,
  isLoadingAvailableSchedule,
  isLoadingFutureScheduleAvailability,
  isLoadingLessonsStatus,
  matchedTutor,
  pendingToBeScheduled,
  productType,
  selectedCourse,
  selectedTimestamp,
  selectedTutor,
  setSelectedDateAction,
  timesAvailableThisDay,
  closeBookingConversationModalAction,
  student,
  timezone
}) => {
  const [endRecordingWholePage] = useInitRecording((timeSpent) =>
    mixpanelHelper.lessonAction('Booked Convo', {
      timeSpent,
      type: 'convo'
    })
  );

  // Auto-select the first available date
  useEffect(() => {
    if (daysAvailableThisMonth?.length > 0 && !selectedTimestamp && !isLoadingAvailableSchedule) {
      const firstAvailableDate = daysAvailableThisMonth[0];
      setSelectedDateAction(firstAvailableDate);
    }
  }, [daysAvailableThisMonth, selectedTimestamp, isLoadingAvailableSchedule, setSelectedDateAction]);

  const executeAppointmentCreation = (appointmentData) => {
    bookConversationClassAction({
      appointmentData,
      courseId: selectedCourse?.id,
      token: student.token,
      successCallback: () => {
        endRecordingWholePage();
      }
    });
  };

  const handleCloseModal = () => {
    closeBookingConversationModalAction(false);
  };

  const handleAppointmentCreation = () => {
    const appointmentData = {
      auto_accept: student.is_staff, // TO-DO: missing group session
      courseId: selectedCourse?.id,
      date: getDateFromIsoString(selectedTimestamp),
      duration: productType.duration,
      // invitees: undefined, // TO-DO: missing group session
      // inviteeNames: undefined, // TO-DO: missing group session
      is_for_group_sessions: false, // TO-DO: missing group session
      is_student_beta_tester: student.is_beta_tester,
      language: selectedCourse?.language.language,
      // lesson_number: 0,
      phone_number: '', // TO-DO: is it always an empty string?
      product_type_id: productType.productTypeId,
      start_time: getTimeFromIsoString(selectedTimestamp),
      status: 'booked',
      student_comment: '', // TO-DO: is it always an empty string?
      time_zone: timezone,
      tutor_id: selectedTutor.id || matchedTutor.id
    };

    executeAppointmentCreation(appointmentData);
  };

  const Alert = <SessionsAlert availableSessionsPerMonth={availableSessionsThisMonth} monthName={getMonthTranslated(currentLang)[calendar?.month]} />;

  return (
    <>
      <SessionsManagementModal
        calendarSubtitle=""
        disabledActionButton={!selectedTimestamp || !timesAvailableThisDay?.some((time) => time === selectedTimestamp)}
        futureSchedule={[pendingToBeScheduled[0]]}
        handleExecution={handleAppointmentCreation}
        isExecuting={isCreatingAppointment}
        isLoading={isLoadingFutureScheduleAvailability || isLoadingLessonsStatus}
        modalMainColor="salmon"
        modalTitle="Booking Convo Practice"
        onCloseModal={() => {
          handleCloseModal();
          mixpanel.closedModal('Booking');
        }}
        productType={productType}
        type={CLASS_TYPES.conversation}
        progressBarColor="salmon"
        renderAlert={() => Alert}
        renderAppointmentItem={(lesson, index) => (
          <BookingSummaryItem
            active
            borderColor="salmon"
            key={index}
            lesson={lesson}
            finalTimestamp={selectedTimestamp}
            testId="schedule-session"
            timezone={timezone}
          />
        )}
        selectedTimestamp={selectedTimestamp}
        testId="booking-convo-session-modal"
      />
      <ErrorToast errorMsg={errorMessage} show={hasErrorOnBooking} onClose={cleanErrorStateAction} />
    </>
  );
};

const mapStateToProps = (state) => ({
  availableSessionsThisMonth: state.bookable.available.availableSessionsThisMonth,
  calendar: state.bookable.available.calendar,
  daysAvailableThisMonth: state.bookable.available.daysAvailableThisMonth,
  errorMessage: state.newBooking.bookingConversation.error,
  hasErrorOnBooking: state.newBooking.bookingConversation.hasErrorOnBooking,
  isLoadingAvailableSchedule: state.bookable.available.isLoadingBookable,
  isLoadingLessonsStatus: state.sessions.conversationSessions.isLoadingLessonsStatus,
  isCreatingAppointment: state.newBooking.bookingConversation.isCreatingAppointment,
  isLoadingFutureScheduleAvailability: state.bookable.futureSchedule.isLoadingFutureSchedule,
  matchedTutor: state.selectedTutor.matchedTutor,
  pendingToBeScheduled: state.newBooking.bookingConversation.appointmentsToBeBooked,
  productType: selectProductType(state.orderInfo, CLASS_TYPES.conversation),
  selectedCourse: state.courses?.selectedCourseDetails?.courseDetails,
  selectedTimestamp: state.bookable.available.calendar?.selectedTimestamp,
  selectedTutor: state.selectedTutor.selectedTutor,
  student: state.user,
  timesAvailableThisDay: state.bookable.available.timesAvailableThisDay,
  timezone: state.time.timezone
});

BookingConvoClassModal.propTypes = {
  availableSessionsThisMonth: PropTypes.object.isRequired,
  bookConversationClassAction: PropTypes.func.isRequired,
  calendar: PropTypes.object.isRequired,
  cleanErrorStateAction: PropTypes.func.isRequired,
  closeBookingConversationModalAction: PropTypes.func.isRequired,
  currentLang: PropTypes.string,
  currentTutor: PropTypes.object,
  daysAvailableThisMonth: PropTypes.array.isRequired,
  errorMessage: PropTypes.string,
  hasErrorOnBooking: PropTypes.bool.isRequired,
  isCreatingAppointment: PropTypes.bool,
  isLoadingAvailableSchedule: PropTypes.bool.isRequired,
  isLoadingFutureScheduleAvailability: PropTypes.bool,
  isLoadingLessonsStatus: PropTypes.bool,
  matchedTutor: PropTypes.object.isRequired,
  pendingToBeScheduled: PropTypes.array.isRequired,
  productType: PropTypes.object.isRequired,
  selectedCourse: PropTypes.object.isRequired,
  selectedTimestamp: PropTypes.string,
  selectedTutor: PropTypes.object.isRequired,
  setSelectedDateAction: PropTypes.func.isRequired,
  student: PropTypes.object.isRequired,
  timesAvailableThisDay: PropTypes.array.isRequired,
  timezone: PropTypes.string.isRequired
};

BookingConvoClassModal.defaultProps = {
  currentLang: 'en',
  isCreatingAppointment: false,
  isLoadingFutureScheduleAvailability: false,
  isLoadingLessonsStatus: false
};

export default connect(mapStateToProps, {
  bookConversationClassAction: bookConversationClass,
  closeBookingConversationModalAction: closeBookingConversationModal,
  cleanErrorStateAction: cleanErrorState,
  setSelectedDateAction: setSelectedDate
})(BookingConvoClassModal);
